import Button from "@material-ui/core/Button";
import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import AssignProductDialog from "@saleor/components/AssignProductDialog";
import NotFoundPage from "@saleor/components/NotFoundPage";
import RecommendProductDialog from "@saleor/components/RecommendProductDialog";
import { RecommendProductTab } from "@saleor/components/RecommendProductSection";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA, PAGINATE_BY } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { createPaginationState } from "@saleor/hooks/usePaginator";
// import usePaginator, {
//   createPaginationState
// } from "@saleor/hooks/usePaginator";
import { commonMessages } from "@saleor/intl";
import { useUnassignRecommendProductMutation } from "@saleor/products/mutations";
import useProductSearch from "@saleor/searches/useProductSearch";
import { ReorderEvent } from "@saleor/types";
import {
  RecommendationTargetTypeEnum,
  RecommendationTypeEnum
} from "@saleor/types/globalTypes";
import { getProductErrorMessage } from "@saleor/utils/errors";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createMetadataUpdateHandler from "@saleor/utils/handlers/metadataUpdateHandler";
import { move } from "@saleor/utils/lists";
import { mapEdgesToItems } from "@saleor/utils/maps";
import {
  useMetadataUpdate,
  usePrivateMetadataUpdate
} from "@saleor/utils/metadata/updateMetadata";
import copy from "copy-to-clipboard";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { getMutationState, maybe } from "../../misc";
import { productUrl } from "../../products/urls";
import { CollectionInput } from "../../types/globalTypes";
import CollectionDetailsPage, {
  CollectionDetailsPageFormData
} from "../components/CollectionDetailsPage/CollectionDetailsPage";
import {
  useCollectionAssignProductMutation,
  useCollectionRemoveMutation,
  useCollectionReorderProducts,
  useCollectionUpdateMutation,
  useCollectionUpdateWithHomepageMutation,
  useUnassignCollectionProductMutation
} from "../mutations";
import { TypedCollectionDetailsQuery } from "../queries";
import { CollectionUpdate } from "../types/CollectionUpdate";
import {
  collectionListUrl,
  collectionUrl,
  CollectionUrlDialog,
  CollectionUrlQueryParams
} from "../urls";

interface CollectionDetailsProps {
  id: string;
  params: CollectionUrlQueryParams;
  isBuytogether: boolean;
}

export function getActiveTab(tabName: string): RecommendProductTab {
  return tabName === RecommendProductTab.also_likes
    ? RecommendProductTab.also_likes
    : RecommendProductTab.recommends;
}

export const CollectionDetails: React.FC<CollectionDetailsProps> = ({
  id,
  params,
  isBuytogether
}) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  // const paginate = usePaginator();
  const intl = useIntl();
  const { search, loadMore, result } = useProductSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const [updateMetadata] = useMetadataUpdate({});
  const [updatePrivateMetadata] = usePrivateMetadataUpdate({});

  const handleCollectionUpdate = (data: CollectionUpdate) => {
    if (data.collectionUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
      navigate(collectionUrl(id));
    } else {
      const backgroundImageError = data.collectionUpdate.errors.find(
        error => error.field === ("backgroundImage" as keyof CollectionInput)
      );
      if (backgroundImageError) {
        notify({
          status: "error",
          text: intl.formatMessage(commonMessages.somethingWentWrong)
        });
      }
    }
  };
  const [updateCollection, updateCollectionOpts] = useCollectionUpdateMutation({
    onCompleted: handleCollectionUpdate
  });

  const [
    updateCollectionWithHomepage,
    updateCollectionWithHomepageOpts
  ] = useCollectionUpdateWithHomepageMutation({
    onCompleted: data => {
      if (data.homepageCollectionUpdate.errors.length === 0) {
        handleCollectionUpdate(data);
      }
    }
  });

  const [removeCollection, removeCollectionOpts] = useCollectionRemoveMutation({
    onCompleted: data => {
      if (data.collectionDelete.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Deleted Recommendation Group"
          })
        });
        navigate(collectionListUrl());
      }
    }
  });

  const [unassignRecommendProduct] = useUnassignRecommendProductMutation({
    onCompleted: data => {
      if (data.recommendProductRemove.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Deleted recommend product from product."
          })
        });
        reset();
        // closeModal();
      }
    }
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    CollectionUrlDialog,
    CollectionUrlQueryParams
  >(navigate, params => collectionUrl(id, params), params);

  const paginationState = createPaginationState(PAGINATE_BY, params);
  const handleBack = () => navigate(collectionListUrl());

  return (
    <TypedCollectionDetailsQuery
      displayLoader
      variables={{ id, ...paginationState }}
    >
      {({ data, loading, refetch }) => {
        const [
          assignProduct,
          assignProductOpts
        ] = useCollectionAssignProductMutation({
          onCompleted: data => {
            if (data.collectionAddProducts.errors.length === 0) {
              notify({
                status: "success",
                text: intl.formatMessage({
                  defaultMessage: "Added product to Recommendation Groups"
                })
              });
              navigate(collectionUrl(id), true);
              refetch();
            }
          }
        });
        const [
          unassignProduct,
          unassignProductOpts
        ] = useUnassignCollectionProductMutation({
          onCompleted: data => {
            if (data.collectionRemoveProducts.errors.length === 0) {
              notify({
                status: "success",
                text: intl.formatMessage({
                  defaultMessage: "Deleted product from Recommendation Groups"
                })
              });
              reset();
              refetch();
              closeModal();
            }
          }
        });
        const collection = data?.collection;

        if (collection === null) {
          return <NotFoundPage onBack={handleBack} />;
        }

        const handleUpdate = async (
          formData: CollectionDetailsPageFormData
        ) => {
          const input: CollectionInput = {
            backgroundImageAlt: formData.backgroundImageAlt,
            descriptionJson: JSON.stringify(formData.description),
            isPublished: formData.isPublished,
            name: formData.name,
            url: formData.url,
            publicationDate: formData.publicationDate,
            seo: {
              description: formData.seoDescription,
              title: formData.seoTitle,
              keyword: formData.seoKeyword
            },
            slug: formData.slug,
            usedFor: formData.usedFor,
            displayOrder: formData.displayOrder,
            showPrompt: formData.showPrompt,
            promptText: formData.promptText,
            promptLink: formData.promptLink,
            promptBackgroundColor: formData.promptBackgroundColor,
            promptTextColor: formData.promptTextColor,
            promptArrowColor: formData.promptArrowColor,
            enablePopupMessage: formData.enablePopupMessage,
            popupMessage: formData.popupMessage
          };
          const isFeatured = data.shop.homepageCollection
            ? data.shop.homepageCollection.id === data.collection.id
            : false;

          if (formData.isFeatured !== isFeatured) {
            const result = await updateCollectionWithHomepage({
              variables: {
                homepageId: formData.isFeatured ? id : null,
                id,
                input
              }
            });
            return [
              ...result.data.collectionUpdate.errors,
              ...result.data.homepageCollectionUpdate.errors
            ];
          } else {
            const result = await updateCollection({
              variables: {
                id,
                input
              }
            });

            return result.data.collectionUpdate.errors;
          }
        };
        const handleSubmit = createMetadataUpdateHandler(
          data?.collection,
          handleUpdate,
          variables => updateMetadata({ variables }),
          variables => updatePrivateMetadata({ variables })
        );

        const formTransitionState = getMutationState(
          updateCollectionOpts.called ||
            updateCollectionWithHomepageOpts.called,
          updateCollectionOpts.loading ||
            updateCollectionWithHomepageOpts.loading,
          updateCollectionOpts.data?.collectionUpdate.errors,
          updateCollectionWithHomepageOpts.data?.collectionUpdate.errors,
          updateCollectionWithHomepageOpts.data?.homepageCollectionUpdate.errors
        );

        // const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
        //   data?.collection?.products?.pageInfo,
        //   paginationState,
        //   params
        // );

        const [collectionReorderProducts] = useCollectionReorderProducts({
          onCompleted: data => {
            if (data.collectionReorderProducts.errors.length !== 0) {
              notify({
                status: "error",
                text: getProductErrorMessage(
                  data.collectionReorderProducts.errors[0],
                  intl
                )
              });
            }
          }
        });

        const handleProductReorder = ({ newIndex, oldIndex }: ReorderEvent) =>
          collectionReorderProducts({
            optimisticResponse: {
              collectionReorderProducts: {
                __typename: "CollectionReorderProducts",
                collection: {
                  ...data.collection,
                  collectionProducts: move(
                    data.collection.collectionProducts[oldIndex],
                    data.collection.collectionProducts,
                    (a, b) => a.id === b.id,
                    newIndex
                  )
                },
                errors: []
              }
            },
            variables: {
              collectionId: id,
              move: {
                productId: data.collection.collectionProducts[oldIndex].id,
                sortOrder: newIndex - oldIndex
              }
            }
          });

        // const changeTab = (tabName: RecommendProductTab) => {
        //   reset();
        //   navigate(
        //     collectionUrl(id, {
        //       activeTab: tabName
        //     })
        //   );
        // };

        const [onProductSelectReorder] = useCollectionReorderProducts({
          onCompleted: data => {
            if (data.collectionReorderProducts.errors.length !== 0) {
              notify({
                status: "error",
                text: getProductErrorMessage(
                  data.collectionReorderProducts.errors[0],
                  intl
                )
              });
              navigate(collectionUrl(id), true);
            }
          }
        });

        const handleProductSelectReorder = (pId: string, sortOrder: number) => {
          onProductSelectReorder({
            variables: {
              collectionId: id,
              move: {
                productId: pId,
                sortOrder
              }
            }
          });
        };

        return (
          <>
            <WindowTitle title={maybe(() => data.collection.name)} />
            <CollectionDetailsPage
              // changeTab={changeTab}
              // currentTab={params.activeTab}
              isBuytogether={isBuytogether}
              onProductReorder={handleProductReorder}
              onProductSelectReorder={handleProductSelectReorder}
              onAdd={() => openModal("assign")}
              onCopyIds={() => {
                openModal("copyids");
              }}
              onBack={handleBack}
              disabled={loading}
              collection={data?.collection}
              errors={updateCollectionOpts?.data?.collectionUpdate.errors || []}
              isFeatured={maybe(
                () => data.shop.homepageCollection.id === data.collection.id,
                false
              )}
              onCollectionRemove={() => openModal("remove")}
              onImageDelete={() => openModal("removeImage")}
              onImageUpload={file =>
                updateCollection({
                  variables: {
                    id,
                    input: {
                      backgroundImage: file
                    }
                  }
                })
              }
              onSubmit={handleSubmit}
              // onNextPage={loadNextPage}
              // onPreviousPage={loadPreviousPage}
              // pageInfo={pageInfo}
              onProductUnassign={(productId, event) => {
                event.stopPropagation();
                unassignProduct({
                  variables: {
                    collectionId: id,
                    productIds: [productId],
                    ...paginationState
                  }
                });
              }}
              onRowClick={id => () => navigate(productUrl(id))}
              onRecommendAssign={() => openModal("assign-recommend")}
              onAlsolikeAssign={() => openModal("assign-alsolike")}
              onBuytogetherAssign={() => openModal("assign-buytogether")}
              onRecommendUnassign={(productId, event) => {
                event.stopPropagation();
                unassignRecommendProduct({
                  variables: {
                    targetId: id,
                    targetType: RecommendationTargetTypeEnum.COLLECTION,
                    recommendType: RecommendationTypeEnum.RECOMMEND,
                    productIds: [productId]
                  }
                });
              }}
              onAlsolikeUnassign={(productId, event) => {
                event.stopPropagation();
                unassignRecommendProduct({
                  variables: {
                    targetId: id,
                    targetType: RecommendationTargetTypeEnum.COLLECTION,
                    recommendType: RecommendationTypeEnum.ALSO_LIKE,
                    productIds: [productId]
                  }
                });
              }}
              onBuytogetherUnAssign={(productId, event) => {
                event.stopPropagation();
                unassignRecommendProduct({
                  variables: {
                    targetId: id,
                    targetType: RecommendationTargetTypeEnum.COLLECTION,
                    recommendType: RecommendationTypeEnum.BUY_TOGETHER,
                    productIds: [productId]
                  }
                });
              }}
              saveButtonBarState={formTransitionState}
              toolbar={
                <Button
                  color="primary"
                  onClick={() =>
                    openModal("unassign", {
                      ids: listElements
                    })
                  }
                >
                  <FormattedMessage
                    defaultMessage="Unassign"
                    description="unassign product from Recommendation Group, button"
                  />
                </Button>
              }
              recommendToolbar={
                <Button
                  color="primary"
                  onClick={() =>
                    openModal("unassign-recommend", {
                      ids: listElements
                    })
                  }
                >
                  <FormattedMessage
                    defaultMessage="Unassign"
                    description="unassign recommend product from product, button"
                  />
                </Button>
              }
              alsolikeToolbar={
                <Button
                  color="primary"
                  onClick={() =>
                    openModal("unassign-alsolike", {
                      ids: listElements
                    })
                  }
                >
                  <FormattedMessage
                    defaultMessage="Unassign"
                    description="unassign alsolike product from product, button"
                  />
                </Button>
              }
              buytogetherToolbar={
                <Button
                  color="primary"
                  onClick={() =>
                    openModal("unassign-buytogether", {
                      ids: listElements
                    })
                  }
                >
                  <FormattedMessage
                    defaultMessage="Unassign"
                    description="unassign alsolike product from product, button"
                  />
                </Button>
              }
              isChecked={isSelected}
              selected={listElements.length}
              toggle={toggle}
              toggleAll={toggleAll}
            />

            <AssignProductDialog
              confirmButtonState={assignProductOpts.status}
              hasMore={result.data?.search?.pageInfo.hasNextPage}
              open={params.action === "assign"}
              onFetch={search}
              onFetchMore={loadMore}
              loading={result.loading}
              onClose={closeModal}
              onSubmit={products =>
                assignProduct({
                  variables: {
                    ...paginationState,
                    collectionId: id,
                    productIds: products.map(product => product.id)
                  }
                })
              }
              products={mapEdgesToItems(result?.data?.search)?.filter(
                suggestedProduct => suggestedProduct.id
              )}
            />
            <ActionDialog
              confirmButtonState={removeCollectionOpts.status}
              onClose={closeModal}
              onConfirm={() =>
                removeCollection({
                  variables: { id }
                })
              }
              open={params.action === "remove"}
              title={intl.formatMessage({
                defaultMessage: "Delete Recommendation Group",
                description: "dialog title"
              })}
              variant="delete"
            >
              <DialogContentText>
                <FormattedMessage
                  defaultMessage="Are you sure you want to delete {collectionName}?"
                  values={{
                    collectionName: (
                      <strong>
                        {maybe(() => data.collection.name, "...")}
                      </strong>
                    )
                  }}
                />
              </DialogContentText>
            </ActionDialog>
            <ActionDialog
              confirmButtonState={unassignProductOpts.status}
              onClose={closeModal}
              onConfirm={() =>
                unassignProduct({
                  variables: {
                    ...paginationState,
                    collectionId: id,
                    productIds: params.ids
                  }
                })
              }
              open={params.action === "unassign"}
              title={intl.formatMessage({
                defaultMessage: "Unassign products from Recommendation Group",
                description: "dialog title"
              })}
            >
              <DialogContentText>
                <FormattedMessage
                  defaultMessage="{counter,plural,one{Are you sure you want to unassign this product?} other{Are you sure you want to unassign {displayQuantity} products?}}"
                  values={{
                    counter: maybe(() => params.ids.length),
                    displayQuantity: (
                      <strong>{maybe(() => params.ids.length)}</strong>
                    )
                  }}
                />
              </DialogContentText>
            </ActionDialog>
            <ActionDialog
              confirmButtonState={updateCollectionOpts.status}
              onClose={closeModal}
              onConfirm={() =>
                updateCollection({
                  variables: {
                    id,
                    input: {
                      backgroundImage: null
                    }
                  }
                })
              }
              open={params.action === "removeImage"}
              title={intl.formatMessage({
                defaultMessage: "Delete image",
                description: "dialog title"
              })}
              variant="delete"
            >
              <DialogContentText>
                <FormattedMessage defaultMessage="Are you sure you want to delete Recommendation Group's image?" />
              </DialogContentText>
            </ActionDialog>
            <RecommendProductDialog
              targetId={id}
              targetType={RecommendationTargetTypeEnum.COLLECTION}
              closeModal={closeModal}
              reset={reset}
              action={params.action}
              ids={params.ids}
            />
            <ActionDialog
              confirmButtonState={updateCollectionOpts.status}
              onClose={closeModal}
              onConfirm={() => {
                copy(data?.collection?.collectionVariantIds);
                notify({
                  status: "success",
                  text: "Copy ids successfully"
                });
                closeModal();
              }}
              open={params.action === "copyids"}
              title={intl.formatMessage({
                defaultMessage: "Copy default variant ids",
                description: "dialog title"
              })}
            >
              <DialogContentText>
                <FormattedMessage
                  defaultMessage="{data}"
                  values={{
                    data: (
                      <strong>
                        {maybe(
                          () => data?.collection?.collectionVariantIds,
                          "..."
                        )}
                      </strong>
                    )
                  }}
                />
              </DialogContentText>
            </ActionDialog>
          </>
        );
      }}
    </TypedCollectionDetailsQuery>
  );
};
export default CollectionDetails;
